/**
 * Bottom Navigation Component for Mobile
 * Shared component that appears on all authenticated pages
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  UserPlus, 
  MessageSquare, 
  User
} from 'lucide-react';
import useUnreadMessages from '../../features/messaging/hooks/useUnreadMessages';
import useConnectionNotifications from '../../features/connect/hooks/useConnectionNotifications';

/**
 * BottomNavigation component for mobile devices
 * @returns {JSX.Element} Bottom navigation component
 */
const BottomNavigation = () => {
  const location = useLocation();
  const unreadMessageCount = useUnreadMessages();
  const { unreadConnectionCount } = useConnectionNotifications();

  // Check if current path is active
  const isActive = (path) => {
    if (path === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    if (path === '/social') {
      return location.pathname === '/social' || location.pathname.startsWith('/social');
    }
    return location.pathname === path;
  };

  // Get active class for navigation items
  const getNavItemClass = (path) => {
    const baseClass = "flex flex-col items-center py-2 px-4";
    const activeClass = "text-green-500";
    const inactiveClass = "text-gray-500";
    
    return `${baseClass} ${isActive(path) ? activeClass : inactiveClass}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-10 md:hidden">
      <div className="flex justify-around">
        <Link to="/home" className={getNavItemClass('/home')}>
          <Home size={22} />
          <span className="text-xs mt-0.5">Home</span>
        </Link>
        
        <Link to="/connect" className={getNavItemClass('/connect')}>
          <div className="relative">
            <UserPlus size={22} />
            {unreadConnectionCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadConnectionCount > 9 ? '9+' : unreadConnectionCount}
              </span>
            )}
          </div>
          <span className="text-xs mt-0.5">Connect</span>
        </Link>
        
        <Link to="/social" className={getNavItemClass('/social')}>
          <MessageSquare size={22} />
          <span className="text-xs mt-0.5">Social</span>
        </Link>
        
        <Link to="/messages" className={getNavItemClass('/messages')}>
          <div className="relative">
            <MessageSquare size={22} />
            {unreadMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
              </span>
            )}
          </div>
          <span className="text-xs mt-0.5">Messages</span>
        </Link>
        
        <Link to="/profile" className={getNavItemClass('/profile')}>
          <User size={22} />
          <span className="text-xs mt-0.5">Profile</span>
        </Link>
      </div>
    </div>
  );
};

export default BottomNavigation;